import React, { useState } from "react";
import { learnAssetMeta, learnAssetSrcSet, learnAssetUrl } from "./learnAssets";

/**
 * Hosted Learn image with 1x/2x srcSet, blur-up placeholder for backgrounds,
 * async decode and a soft fade-in. `fit="cover"` for backgrounds, "contain"
 * for sprites. Never layout-shifts when `aspect` or explicit size is given.
 */
export default function LearnImage({
  asset,
  alt = "",
  fit = "contain",
  eager = true,   // opt out with lazy for long off-screen lists
  lazy = false,
  className,
  style,
  imgStyle,
  onLoad,
  ...rest
}) {
  const [loaded, setLoaded] = useState(false);
  const meta = learnAssetMeta(asset);
  const src = learnAssetUrl(asset);
  const blur = meta?.blur ? learnAssetUrl(asset, { size: "blur" }) : null;
  const decorative = !alt;

  return (
    <div
      className={className}
      style={{ position: "relative", overflow: "hidden", ...style }}
      {...rest}
    >
      {blur && !loaded && (
        <img
          src={blur}
          alt=""
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: fit,
            filter: "blur(14px)",
            transform: "scale(1.08)",
          }}
        />
      )}
      <img
        ref={(el) => {
          // cached images can finish before React attaches onLoad
          if (el && el.complete && el.naturalWidth > 0 && !loaded) setLoaded(true);
        }}
        src={src}
        srcSet={learnAssetSrcSet(asset)}
        alt={alt}
        aria-hidden={decorative ? "true" : undefined}
        draggable={false}
        loading={lazy ? "lazy" : "eager"}
        decoding="async"
        fetchPriority={eager && !lazy ? "high" : undefined}
        onLoad={(e) => {
          setLoaded(true);
          onLoad?.(e);
        }}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: fit,
          opacity: loaded ? 1 : 0,
          transition: "opacity 320ms ease",
          pointerEvents: "none",
          ...imgStyle,
        }}
      />
    </div>
  );
}
